<template>
  <AppLayout>
    <h1 class="mb-4 mt-4">Products</h1>

    <div class="mb-6 flex items-center gap-4">
      <SelectButton
        v-model="attentionFilter"
        :options="filterOptions"
        optionLabel="label"
        optionValue="value"
      />

      <a
        v-if="auth.user.value?.superAdmin"
        href="/api/images/download-attention-images"
        target="_blank"
        >Download Attention Images</a
      >

      <Button variant="outlined" severity="secondary" @click="uploadFixedImagesDialogVisible = true"
        >Upload Fixed Images</Button
      >
    </div>

    <template v-for="day in productsByDay" :key="day.date">
      <h2 class="mb-4">{{ day.date }}</h2>

      <div v-if="day.products.length === 0" class="text-surface-500 mb-4">(No products)</div>

      <div class="flex flex-row flex-wrap gap-2 mb-4">
        <div v-for="product in day.products" :key="product.id" class="product-card-wrapper group">
          <a :href="`/products/${product.id}`">
            <Panel>
              <template #header>
                <div class="flex items-center w-full gap-2">
                  <span>{{ product.name || product.code || '&nbsp;' }}</span>
                  <Button
                    @click.prevent="toggleAttention(product)"
                    icon="pi pi-exclamation-triangle"
                    text
                    rounded
                    class="ml-auto transition-opacity"
                    :class="{
                      'text-red-500': product.attentionCount > 0,
                      'text-surface-400 dark:text-surface-500 opacity-0 group-hover:opacity-100':
                        product.attentionCount === 0,
                    }"
                    :severity="product.attentionCount > 0 ? 'danger' : 'secondary'"
                  />
                </div>
              </template>
              <div class="text-xs text-surface-500">{{ product.imageCount }} images</div>
              <img
                :src="getImageUrl(product.imageUrl, product.imageContentType, 250)"
                loading="lazy"
                class="w-36 h-auto"
              />
            </Panel>
          </a>
        </div>
      </div>
    </template>

    <div class="flex justify-center my-9">
      <Button variant="outlined" @click="loadProducts">Load more</Button>
    </div>

    <UploadImagesDialog v-model:visible="uploadFixedImagesDialogVisible" />
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AppLayout from '@/components/AppLayout.vue';
import dayjs from 'dayjs';
import Panel from 'primevue/panel';
import Button from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import type { ListProductsEntryDto } from '@/api/generated';
import { groupBy } from 'lodash';
import { useApiClient } from '@/api/apiClient';
import { useAuth } from '@/modules/auth/auth';
import UploadImagesDialog from '@/modules/products/upload-images-dialog/UploadImagesDialog.vue';

const { api } = useApiClient();
const auth = useAuth();

const loading = ref(true);
const error = ref('');
const products = ref<ListProductsEntryDto[]>([]);

const filterOptions = [
  { label: 'All', value: 'false' },
  { label: 'Attention Only', value: 'true' },
];

const attentionFilter = ref<string>('false');

const uploadFixedImagesDialogVisible = ref(false);

// Watch for filter changes and reset products
watch(attentionFilter, () => {
  products.value = [];
  loadProducts();
});

async function loadProducts() {
  try {
    loading.value = true;
    const { data, error } = await api.productControllerList({
      query: {
        offset: products.value.length,
        limit: 50,
        attention: attentionFilter.value,
      },
    });
    if (error) {
      throw new Error('Failed to load products');
    }
    products.value = products.value.concat(data?.data ?? []);
    loading.value = false;
  } catch (err) {
    error.value = 'Failed to load products';
    throw err;
  } finally {
    loading.value = false;
  }
}

const productsByDay = computed(() => {
  const res = groupBy(products.value, p => dayjs(p.created).format('YYYY-MM-DD'));
  const entries = Object.entries(res).map(([date, products]) => ({
    date,
    products,
  }));
  return entries;
});

loadProducts();

async function toggleAttention(product: ListProductsEntryDto) {
  const attention = product.attentionCount === 0;
  const { error } = await api.productControllerSetAttention({
    path: {
      productId: product.id,
    },
    body: {
      attention,
    },
  });
  if (error) {
    throw new Error('Failed to set attention');
  }
  product.attentionCount = attention ? 1 : 0;
}

function getImageUrl(imageUrl: string, contentType: string, width: number): string | undefined {
  const widthPx = devicePixelRatio * width;
  let url = `${imageUrl}=s${widthPx}`;
  if (contentType === 'image/webp') {
    url += '-rw';
  }
  return url;
}

// async function onProductCreated() {
//   // TODO: how to reset dialog state?
//   reset.value = true;
//   nextTick(() => {
//     reset.value = false;
//   });
// }
</script>
